import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import '@ndrc/ui/theme.css';
import './desk/desk.css';
import './window/window.css';
import './auth/login.css';
import { App } from './App';
import { installSessionBoundary } from './lib/session-boundary';
import { meQueryKey, type MeResult } from './lib/queries';
import { clearPracticeSession } from './practice/usePracticeRequest';
import { setReader } from './messages';
import { installObservedAppFetch } from './host/observation';

/**
 * Le point d'entrée du bureau. Trois lignes de vraie décision :
 *
 * - `retry: false` — une session expirée ne doit pas être retentée trois fois avant de ramener à
 *   l'écran de connexion, et une erreur de portée ne devient jamais vraie en réessayant.
 * - `refetchOnWindowFocus: false` — en classe, on change de fenêtre en permanence ; recharger
 *   toutes les listes à chaque retour ferait clignoter l'écran sans rien apprendre.
 * - Les feuilles de style sont importées **ici**, dans l'ordre : les jetons d'abord, le chrome
 *   ensuite. Un composant n'importe jamais un style dont un autre composant dépend.
 */
const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: false, refetchOnWindowFocus: false } },
});

/**
 * Avant tout montage : le bureau décore les requêtes de ses apps hébergées tant qu'un enseignant
 * lit le travail d'un élève (`host/observation.ts`). Hors observation — donc pour tous les élèves,
 * tout le temps — la fonction d'origine est appelée telle quelle : aucune requête de la plateforme
 * ne traverse un octet de code supplémentaire.
 */
installObservedAppFetch();

const container = document.getElementById('root');
if (container === null) {
  throw new Error("Le conteneur #root est absent d'index.html : le bureau ne peut pas démarrer.");
}

const root = createRoot(container);
installSessionBoundary(() => {
  const me = queryClient.getQueryData<MeResult>(meQueryKey);
  if (me && me.role !== 'student') clearPracticeSession(me.id);
  // Unmount first: an offline navigation must never leave a private screen painted.
  root.unmount();
  queryClient.clear();
  setReader('student');
  window.location.replace('/login');
});
root.render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
